import {flow, types, cast} from 'mobx-state-tree';
import {toJS} from 'mobx';
import {getSingleUrlParam} from '@/utils/url';
import {INDEXING_API} from '@/api/indexing/indexing-apis';
import {notification} from '@/utils/notification';
import html2canvas from 'html2canvas';

// Default filter configurations for indexing data
const URL_INDEXING_FILTER = [
  {id: 1, name: 'url', header: 'URL', filterField: 'url', text: '', active: false},
  {id: 2, name: 'radioButtonsFilter', header: 'Status', customCategoryType: 'status', filterTypes: [
    {label: 'In Progress', value: 'in_progress'},
    {label: 'Completed', value: 'completed'},
    {label: 'Unindexed', value: 'unindexed'},
    {label: 'Indexed', value: 'indexed'},
  ], type: undefined, active: false},
  {id: 3, name: 'batch', header: 'Batch', filterField: 'batch', text: '', active: false},
  {id: 4, name: 'indexedLinks', header: 'Indexed links', filterField: 'indexed_links', text: '', active: false},
  {id: 5, name: 'submittedLinks', header: 'Submitted links', filterField: 'submitted_links', text: '', active: false},
  {id: 6, name: 'search_simple', header: 'Search', text: '', type: undefined, active: false, isSearch: true},
  {id: 7, name: 'unindexedLinks', header: 'Unindexed links', from: undefined, to: undefined, type: undefined, active: false},
  {id: 8, name: 'totalLinks', header: 'Total links', from: undefined, to: undefined, type: undefined, active: false},
];
export const UrlIndexingDataList = types.model({
  results: types.array(types.frozen()),
  currentPage: types.optional(types.number, 1),
  totalPages: types.optional(types.number, 1),
  totalCount: types.optional(types.number, 0),
  nextPage: types.maybeNull(types.number),
  previousPage: types.maybeNull(types.number),
});

export const UrlIndexingHistoryList = types.model({
  results: types.array(types.frozen()),
  currentPage: types.optional(types.number, 1),
  totalPages: types.optional(types.number, 1),
  totalCount: types.optional(types.number, 0),
  nextPage: types.maybeNull(types.number),
  previousPage: types.maybeNull(types.number),
});
export const IndexingHistory = types.model({
  date: types.optional(types.string, ''),
  indexingHistory: types.optional(types.model({
    id: types.optional(types.number, 0),
    name: types.optional(types.string, ''),
    status: types.optional(types.string, ''),
    createdAt: types.optional(types.string, ''),
    updatedAt: types.optional(types.string, ''),
    indexedLinks: types.optional(types.number, 0),
    processedLinks: types.optional(types.number, 0),
    submittedLinks: types.optional(types.number, 0),
    inProgress: types.optional(types.number, 0),
    notIndexed: types.optional(types.number, 0),
  }), {}),
});

export const UrlIndexingChartList = types.model({
  historicalStatus: types.array(IndexingHistory),
  currentStatus: types.optional(types.model({
    id: types.optional(types.number, 0),
    name: types.optional(types.string, ''),
    status: types.optional(types.string, ''),
    createdAt: types.optional(types.string, ''),
    updatedAt: types.optional(types.string, ''),
    isReportAvailable: types.optional(types.boolean, false),
    inProgress: types.optional(types.number, 0),
    notIndexed: types.optional(types.number, 0),
    indexedLinks: types.optional(types.number, 0),
    processedLinks: types.optional(types.number, 0),
    submittedLinks: types.optional(types.number, 0),
  }), {}),
});
export const urlIndexingStore = types.model({
  urlIndexingDataList: types.array(types.frozen(UrlIndexingDataList)),
  urlIndexingHistoryList: types.array(types.frozen(UrlIndexingHistoryList)),
  urlIndexingChartList: types.array(types.frozen(UrlIndexingChartList)),
  loadingList: types.boolean,
  loadingHistoryList: types.boolean,
  chartHistoryLoading: types.boolean,
  isExporting: types.boolean,
  isExportingSingleNode: types.boolean,
  urlIndexingFilter: types.array(types.frozen(URL_INDEXING_FILTER)),
  params: types.model({
    page_size: types.optional(types.number, 10),
    page: types.optional(types.number, 1),
    status: types.optional(types.string, ''),
    search: types.optional(types.string, ''),
    indexing_batch: types.optional(types.string, ''),
    unindexed_links: types.optional(types.string, ''),
    total_links: types.optional(types.string, ''),
    submitted_links: types.optional(types.string, ''),
    ordering: types.optional(types.string, ''),
    url: types.optional(types.string, ''),
  }),
  pagination: types.model({
    current_page: types.optional(types.number, 1),
    total_pages: types.optional(types.number, 1),
    next_page: types.maybeNull(types.number),
    previous_page: types.maybeNull(types.number),
    count: types.optional(types.number, 0),
  }),
  urlIndexingBatchByIdFilters: types.array(types.frozen({
    url: types.optional(types.string, ''),
    status: types.optional(types.string, ''),
    batch: types.optional(types.string, ''),
    indexed_links: types.optional(types.string, ''),
    submitted_links: types.optional(types.string, ''),
    unindexed_links: types.optional(types.string, ''),
    total_links: types.optional(types.string, ''),
    page_size: types.optional(types.number, 10),
    page: types.optional(types.number, 1),
    ordering: types.optional(types.string, ''),
  })),

})
  .views(self => ({
    get getUrlIndexingData() {
      return toJS(self.urlIndexingDataList);
    },
    get getUrlIndexingHistoryData() {
      return toJS(self.urlIndexingHistoryList);
    },
    get getUrlIndexingChartData() {
      return toJS(self.urlIndexingChartList);
    },
    get getUrlIndexingFilter() {
      return toJS(self.urlIndexingFilter);
    },
    get getUrlIndexingBatchByIdFilters() {
      return toJS(self.urlIndexingBatchByIdFilters);
    },
    get getPaginationInfo() {
      return toJS(self.pagination);
    },
  }))
  .actions(self => {
    const setLoadingList = loading => {
      self.loadingList = loading;
    };
    const setLoadingHistoryList = loading => {
      self.loadingHistoryList = loading;
    };
    const setChartHistoryLoading = loading => {
      self.chartHistoryLoading = loading;
    };
    const setIndexingFilters = filters => {
      self.urlIndexingFilter = filters;
    };
    const setIsExporting = (isExporting: boolean) => {
      self.isExporting = isExporting;
    };

    const setIsExportingSingleNode = (isExportingSingleNode: boolean) => {
      self.isExportingSingleNode = isExportingSingleNode;
    };
    const setUrlIndexingBatchByIdFilters = filters => {
      self.urlIndexingBatchByIdFilters = filters;
    };
    const loadUrlIndexingData = flow(function* (params: any = {}, noLoading = false) {
      const publicHash = getSingleUrlParam('public_hash');
      if (!noLoading) {
        self.loadingList = true;
      }

      const formattedFilters = {};
      self.urlIndexingFilter.forEach((filter: any) => {
        if (filter.active) {
          if (filter.name === 'status' && filter.type) {
            formattedFilters[filter.filterField] = filter.type;
          } else if (filter.name === 'url' && filter.text) {
            formattedFilters[filter.filterField] = filter.text;
          } else if (filter.name === 'batch' && filter.text) {
            formattedFilters[filter.filterField] = filter.text;
          } else if (filter.name === 'unindexedLinks' && filter.from && filter.to) {
            formattedFilters[`${filter.filterField}_from`] = filter.from;
            formattedFilters[`${filter.filterField}_to`] = filter.to;
          } else if (filter.name === 'submittedLinks' && filter.from && filter.to) {
            formattedFilters[`${filter.filterField}_from`] = filter.from;
            formattedFilters[`${filter.filterField}_to`] = filter.to;
          } else if (filter.name === 'SubmittedOn' && filter.dateValue) {
            formattedFilters[`${filter.filterField}`] = filter.dateValue;
          }
        }
      });

      // Ensure pagination and ordering parameters are always set
      formattedFilters['page_size'] = params.page_size || self.params.page_size || 10;
      formattedFilters['page'] = params.page || self.params.page || 1;
      if (params.ordering || self.params.ordering) {
        formattedFilters['ordering'] = params.ordering || self.params.ordering;
      }

      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          formattedFilters[key] = value;
        }
      });

      try {
        setLoadingList(true);
        const response = yield INDEXING_API.loadIndexingBatches(formattedFilters, publicHash);
        if (response && response.results) {
          self.urlIndexingDataList = cast(response.results);
          self.pagination = cast({
            current_page: response.currentPage,
            next_page: response.nextPage,
            previous_page: response.previousPage,
            total_pages: response.totalPages,
            count: response.count,
          });
          return response;
        } else {
          notification.error('No data received:', response);
        }
      } catch (err) {
        self.urlIndexingDataList = cast([]);
        self.pagination = cast({
          current_page: 1,
          next_page: null,
          previous_page: null,
          total_pages: 1,
          count: 0,
        });
        if (err?.response?.status === 400) {
          const errorMessage = err?.response?.data?.siteproperty?.siteproperty || 'Error loading data';
          notification.error('Error loading data', errorMessage);
        }
        if (err?.response?.status >= 500 && err?.response?.status < 600) {
          notification.error('Sorry', 'There was a server error. Please refresh the page');
        }
        return Promise.reject(err?.message || 'Error loading Batch data');
      } finally {
        setLoadingList(false);
      }
    });

    // const setParams = newParams => {
    //   self.params = {...self.params, ...newParams};
    // };

    // Action to create an indexing batch
    const createIndexingBatch = flow(function* (payload, noLoading = false) {
      if (!noLoading) {
        setLoadingList(true);
      }
      try {
        const publicHash = getSingleUrlParam('public_hash');
        const response = yield INDEXING_API.postIndexing(payload, publicHash);
        return response;
      } catch (e) {
        notification.error('Error creating indexing batch:', e);
      } finally {
        self.loadingList = false;
      }
    });

    // Action to get indexing batch by Id
    const getIndexingBatchById = flow(function* (id, params?: any) {
      setLoadingHistoryList(true);

      try {
        const publicHash = getSingleUrlParam('public_hash');
        const requestParams = {
          page_size: params?.page_size || 10,
          page: params?.page || 1,
          ...params,
        };

        if (!params || Object.keys(params).length === 0) {
          delete requestParams.ordering;
        }

        const response = yield INDEXING_API.getIndexingBatchById(id, requestParams, publicHash);
        self.urlIndexingHistoryList = cast(response.results);
        self.pagination = cast({
          current_page: response.currentPage,
          next_page: response.nextPage,
          previous_page: response.previousPage,
          total_pages: response.totalPages,
          count: response.count,
        });
        return response;
      } catch (e) {
        self.urlIndexingHistoryList = cast([]);
        self.pagination = cast({
          current_page: 1,
          next_page: null,
          previous_page: null,
          total_pages: 1,
          count: 0,
        });
        const errorMessage = e?.response?.data?.requestStatus?.[0] || 'Error getting indexing batch by id';
        notification.error('Error getting indexing batch by id:', errorMessage);
      } finally {
        setLoadingHistoryList(false);
      }
    });

    // Action to get indexing batch history
    const getIndexingChartHistory = flow(function* (id) {
      setChartHistoryLoading(true);
      self.urlIndexingChartList = cast([]);
      try {
        const publicHash = getSingleUrlParam('public_hash');

        const response = yield INDEXING_API.getIndexingChartHistory(id, publicHash);
        if (response) {
          self.urlIndexingChartList = cast([response]);
        }
        return response;
      } catch (err) {
        self.urlIndexingChartList = cast([]);
        if (err?.response?.status === 400) {
          const errorMessage = err?.response?.data?.siteproperty?.siteproperty || 'Error loading data';
          notification.error('Error loading data', errorMessage);
        }
        if (err?.response?.status >= 500 && err?.response?.status < 600) {
          notification.error('Sorry', 'There was a server error. Please refresh the page');
        }
        return Promise.reject(err?.message || 'Error getting indexing chart history');
      } finally {
        setChartHistoryLoading(false);
      }
    });

    // Action to export indexing batch
    const exportIndexingBatch = flow(function* (id) {
      self.isExporting = true;
      try {
        const response = yield INDEXING_API.exportIndexingBatches(id);
        return response;
      } catch (e) {
        const errorMessage = e?.message || 'Error exporting indexing batch';
        notification.error('Error exporting indexing batch:', errorMessage);
        throw e;
      } finally {
        self.isExporting = false;
      }
    });

    const exportUrlIndexingBatch = flow(function* (id) {
      self.isExportingSingleNode = true;
      try {
        const response = yield INDEXING_API.exportIndexingBatches(id);
        return response;
      } catch (e) {
        const errorMessage = e?.message || 'Error exporting indexing batch';
        notification.error('Error exporting indexing batch:', errorMessage);
        throw e;
      } finally {
        self.isExportingSingleNode = false;
      }
    });
    const detailOverviewExport = flow(function* (node) {
      self.isExportingSingleNode = true;
      try {
        let JSpdf = null;
        if (typeof window !== 'undefined') {
          import('jspdf').then(module => {
            JSpdf = module.default;
          });
        }
        const today = new Date();
        const date = today.getFullYear()+'-'+(today.getMonth()+1)+'-'+today.getDate();
        yield html2canvas(node, {
          allowTaint: true,
          useCORS: true,
          windowWidth: document.documentElement.offsetWidth,
          windowHeight: document.documentElement.offsetHeight,
          scale: 2,
        })
          .then(canvas => {
            canvas.style.display = 'none';
            const image = canvas.toDataURL('image/jpeg', 1.0);

            const screenWidth = window.innerWidth;
            const screenHeight = window.innerHeight;

            const pdfWidth = screenWidth;
            const pdfHeight = screenHeight;

            const pdf = new JSpdf({
              orientation: screenWidth > screenHeight ? 'l' : 'p',
              unit: 'pt',
              format: [pdfWidth, pdfHeight],
              compress: true,

            });

            const scale = Math.min(pdfWidth / canvas.width, pdfHeight / canvas.height);
            const scaledWidth = canvas.width * scale;
            const scaledHeight = canvas.height * scale;
            const x = (pdfWidth - scaledWidth) / 2;
            const y = (pdfHeight - scaledHeight) / 2;

            pdf.addImage(image, 'JPEG', x, y, scaledWidth, scaledHeight, null, 'FAST');
            pdf.save(`URL-Indexing-${date}.pdf`);
          });
      } catch (e) {
        notification.error('Error exporting indexing batch:', e);
        throw e;
      } finally {
        self.isExportingSingleNode = false;
      }
    });


    return {
      loadUrlIndexingData,
      // setParams,
      createIndexingBatch,
      getIndexingBatchById,
      getIndexingChartHistory,
      exportIndexingBatch,
      setLoadingHistoryList,
      setChartHistoryLoading,
      setLoadingList,
      setIndexingFilters,
      setUrlIndexingBatchByIdFilters,
      setIsExporting,
      detailOverviewExport,
      setIsExportingSingleNode,
      exportUrlIndexingBatch,
    };
  });

export const initUrlIndexingStore = () => {
  return urlIndexingStore.create({
    urlIndexingDataList: [],
    urlIndexingHistoryList: [],
    urlIndexingChartList: [],
    loadingList: false,
    loadingHistoryList: false,
    chartHistoryLoading: false,
    urlIndexingFilter: [URL_INDEXING_FILTER],
    urlIndexingBatchByIdFilters: [],
    params: {
      page_size: 10,
      page: 1,
      status: '',
      search: '',
      indexing_batch: '',
      unindexed_links: '',
      total_links: '',
      submitted_links: '',
      ordering: '',
    },
    pagination: {
      current_page: 1,
      total_pages: 1,
      next_page: null,
      previous_page: null,
    },
    isExporting: false,
    isExportingSingleNode: false,
  });
};
